import { IonButton, IonButtons, IonContent, IonHeader, IonText, IonTitle, IonToolbar, useIonModal } from '@ionic/react';
import {
  forwardRef,
  ForwardRefRenderFunction,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';

import styles from '@components/Modal.module.css';
import Icon from '@components/text/Icon';
import { Opener } from '@hooks/useOpener';
import { clickCurrentTarget } from '@utils/keyboardUtils';

type ChildrenData = {
  modalDisplayed: boolean;
};

type Props = {
  children: (data: ChildrenData) => ReactNode;
  cssClass?: string;
  dataCy?: string;
  fullWidth?: boolean;
  hideHeader?: boolean;
  hideTitleBorder?: boolean;
  onCancel?: () => void;
  opener: Opener;
  title?: string;
};

export type ModalElement = {
  scrollToTop: () => void;
};

type ModalBodyProps = {
  children: (data: ChildrenData) => ReactNode;
  contentRef: React.RefObject<HTMLIonContentElement>;
  dataCy?: string;
  fullWidth?: boolean;
  hideHeader?: boolean;
  hideTitleBorder?: boolean;
  modalDisplayed: boolean;
  opener: Opener;
  title?: string;
};

const ModalBody = ({
  children,
  contentRef,
  dataCy,
  fullWidth,
  hideHeader = false,
  hideTitleBorder,
  modalDisplayed,
  opener,
  title
}: ModalBodyProps) => (
  <>
    {!hideHeader && (
      <IonHeader
        className={hideTitleBorder ? styles.headerWithoutBorder + ' ion-no-border' : styles.header + ' ion-no-border'}
        data-test={dataCy}
      >
        <IonToolbar className={styles.titleToolbar}>
          {title && (
            <IonTitle className={styles.title}>
              <IonText>{title}</IonText>
            </IonTitle>
          )}
          <IonButtons slot="end">
            <IonButton
              className={styles.closeButton}
              data-test="close-modal"
              onClick={opener.close}
              onKeyUp={clickCurrentTarget}
              tabIndex={0}
            >
              <Icon ariaLabelKey="accessibility.ariaLabels.close" icon="times" />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
    )}
    <IonContent data-test={dataCy} ref={contentRef}>
      <div
        className={
          fullWidth
            ? styles.fullWidthContentWrapper
            : hideTitleBorder
            ? styles.lessPaddingContentWrapper
            : styles.contentWrapper
        }
      >
        {children({ modalDisplayed })}
      </div>
    </IonContent>
  </>
);

const Modal: ForwardRefRenderFunction<ModalElement, Props> = (
  { children, cssClass, dataCy, fullWidth = false, hideHeader = false, hideTitleBorder, onCancel, opener, title },
  ref
) => {
  const contentRef = useRef<HTMLIonContentElement>(null);

  useImperativeHandle(ref, () => ({
    scrollToTop: () => {
      if (contentRef.current) {
        contentRef.current.scrollToTop(2000);
      }
    }
  }));

  const [modalDisplayed, setModalDisplayed] = useState<boolean>(false);

  const [presentModal, dismissModal] = useIonModal(ModalBody, {
    children,
    contentRef,
    dataCy,
    fullWidth,
    hideHeader,
    hideTitleBorder,
    modalDisplayed,
    opener,
    title
  });

  useEffect(
    () => () => {
      dismissModal();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleDidDismiss = useCallback(() => {
    if (onCancel) {
      onCancel();
    }
    opener.close();
    setModalDisplayed(false);
  }, [onCancel, opener]);

  const handleDidPresent = useCallback(() => {
    setModalDisplayed(true);
  }, []);

  const handleWillDismiss = useCallback(() => {
    setModalDisplayed(false);
  }, []);

  useEffect(() => {
    if (opener.isOpen) {
      presentModal({
        backdropDismiss: false,
        cssClass: `${cssClass ?? ''} ${styles.roundModal}`,
        onDidDismiss: handleDidDismiss,
        onDidPresent: handleDidPresent,
        onWillDismiss: handleWillDismiss
      });
    } else {
      dismissModal();
    }
  }, [cssClass, dismissModal, handleDidDismiss, handleDidPresent, handleWillDismiss, opener.isOpen, presentModal]);

  return <></>;
};

export default forwardRef(Modal);
